/**
 * LVREF — код платёжного требования в назначении иностранного платежа.
 * Дизайн: docs/FOREIGN_PAYMENT_PURPOSE_FORMAT.md. Код выдаёт только сервер
 * (royalty_issue_payment_reference); здесь — зеркала текста и ASCII-ДКК.
 */

export type RoyaltyPaymentReferenceChargeType =
  | 'ROYALTY'
  | 'SPLIT_ROYALTY'
  | 'SPLIT_AGENCY'
  | 'MANUAL_ROYALTY'
  | 'LISTOK_CRM'
  | 'FIXED_ROYALTY'

/** Лимит назначения Zbooni; один и тот же компактный текст идёт и в банк. */
export const ROYALTY_PAYMENT_REFERENCE_PURPOSE_LIMIT = 45

export const ROYALTY_PAYMENT_REFERENCE_TYPE_TOKEN: Record<RoyaltyPaymentReferenceChargeType, string> = {
  ROYALTY: 'ROY',
  SPLIT_ROYALTY: 'SRY',
  SPLIT_AGENCY: 'SAF',
  MANUAL_ROYALTY: 'MRY',
  LISTOK_CRM: 'CRM',
  FIXED_ROYALTY: 'FRY',
}

// Посимвольная таблица §3.2 по всем символам contracts.contract_number на PROD.
// Зеркало translate() в public.royalty_contract_number_ascii — менять парой.
const CONTRACT_NUMBER_TO_ASCII: Record<string, string> = {
  Н: 'H', П: 'P', Р: 'R', А: 'A', С: 'S', К: 'K', Т: 'T',
  н: 'H', п: 'P', р: 'R', а: 'A', с: 'S', к: 'K', т: 'T',
  '‑': '-', '–': '-', '—': '-', '−': '-',
}

const ASCII_TO_CONTRACT_NUMBER: Record<string, string> = {
  H: 'Н', P: 'П', R: 'Р', A: 'А', S: 'С', K: 'К', T: 'Т',
}

/**
 * Номер ДКК для назначения: «131-П2-Н-ПП» → «131-P2-H-PP». Fail-closed: символ вне
 * [0-9A-Z/-] после замены — null, ДКК в назначение не ставится (ложный номер опаснее
 * его отсутствия).
 */
export function contractNumberToAscii(contractNumber: string | null | undefined): string | null {
  const ascii = Array.from((contractNumber ?? '').trim())
    .map((char) => CONTRACT_NUMBER_TO_ASCII[char] ?? char)
    .join('')
  return /^[0-9A-Z/-]+$/.test(ascii) ? ascii : null
}

/**
 * Обратный разбор номера после маркера DKK: «058-P» → «058-П», «263-R» → «263-Р».
 * Визуальный нормализатор legacy-назначений прочёл бы P как «Р», а R не узнал бы вовсе.
 */
export function contractNumberFromAscii(ascii: string): string {
  return Array.from(ascii.toUpperCase())
    .map((char) => ASCII_TO_CONTRACT_NUMBER[char] ?? char)
    .join('')
}

/**
 * ⚠️ Зеркало public.royalty_payment_request_purpose_lvref: текст обязан совпадать байт
 * в байт (проверяет scripts/dry-run-royalty-payment-reference.ts). Код первым — банк
 * режет хвост назначения и переносит SWIFT-строку на 35-м символе.
 */
export function buildRoyaltyPaymentReferencePurpose(input: {
  referenceCode: string
  contractNumber: string | null | undefined
  year: number
  month: number
  chargeType: RoyaltyPaymentReferenceChargeType
}): string {
  const token = ROYALTY_PAYMENT_REFERENCE_TYPE_TOKEN[input.chargeType]
  const base = `LVREF${input.referenceCode} ${token} ${String(input.month).padStart(2, '0')}.${input.year}`
  const dkk = contractNumberToAscii(input.contractNumber)
  const full = dkk ? `${base} DKK${dkk}` : null
  // ДКК не обрезается и не выдумывается: не поместился — короткая форма.
  return full && full.length <= ROYALTY_PAYMENT_REFERENCE_PURPOSE_LIMIT ? full : base
}

/** Контрольная цифра Luhn для полезной части (зеркало royalty_payment_reference_check_digit). */
export function paymentReferenceCheckDigit(payload: string): number {
  let sum = 0
  let double = true
  for (let i = payload.length - 1; i >= 0; i -= 1) {
    let digit = Number(payload[i])
    if (double) {
      digit *= 2
      if (digit > 9) digit -= 9
    }
    sum += digit
    double = !double
  }
  return (10 - (sum % 10)) % 10
}

export function isValidPaymentReferenceCode(code: string | null | undefined): boolean {
  if (!code || !/^[1-9][0-9]{9}$/.test(code)) return false
  return paymentReferenceCheckDigit(code.slice(0, 9)) === Number(code[9])
}

/** Есть ли в назначении маркер LVREF (то же условие, что `~* 'LV[[:space:]]*REF'` на сервере). */
export function hasPaymentReferenceMarker(purpose: string | null | undefined): boolean {
  return /LV\s*REF/i.test(purpose ?? '')
}
